
import React, { useState, useEffect } from 'react';

import {
  Add
} from "@mui/icons-material";
import { Tooltip, Fab } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  CreatureAbility,
  AbilityEffect,
  UpgradableNumber,
  AttachedTable,
  ConsumedResource, 
} from "@dndModels";
import { 
  ABILITY_SCORES_EXPANDED, 
  DURATIONS,
  COMPONENTS,
  // REFRESH_RULES,
  STANDARD_CASTING_TIMES
} from "@dndModels/Constants";
import { AbilityScoreExpandedType, CastingTime, DurationType } from "@dndModels/Types";

import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import CheckBox from "@components/input/CheckBox";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";

// import SelectObjectBox from '../../../../components/input/selects/SelectObjectBox';
import AbilityEffectInput from "@dndComponents/feature/AbilityEffectInput";
import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";
import ConsumedResourceInput from "@dndComponents/feature/ConsumedResourceInput";

interface Props {
  obj: CreatureAbility;
  onChange: (changed: CreatureAbility) => void; 
  disabled: boolean;
  attached_table?: AttachedTable;
}

export default function CreatureAbilityInput({ 
  obj,
  onChange,
  disabled,
  attached_table
}: Props) {
  const [reloading, setReloading] = useState(false);
  const [show_effect, setShowEffect] = useState("");
  // const [special_refresh_rule, setSpecialRefreshRule] = useState(false);
  const [ping, setPing] = useState(false);
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  // const renderRecharge = () => {
  //   if (obj.resource_consumed === "Recharge") {
  //     const recharge_on_options: string[] = [];
  //     for (let i = 1; i <= obj.recharge_die; ++i) {
  //       recharge_on_options.push(`${i}`);
  //     }
  //     return (
  //       <>
  //         <Grid item xs={12}>
  //           <SelectStringBox  
  //             name="Recharge Die"
  //             options={["4","6","8","10","12","20","100"]}
  //             value={ `${obj.recharge_die}` }
  //             disabled={disabled}
  //             onChange={(changed: string) => {
  //               const new_obj = obj;
  //               new_obj.recharge_die = +changed as 4 | 6 | 8 | 10 | 12 | 20 | 100;
  //               onChange(new_obj);
  //               setReloading(true);
  //             }}
  //           />
  //         </Grid>
  //         <Grid item xs={12}>
  //           <SelectStringBox  
  //             name="Recharge On"
  //             options={recharge_on_options}
  //             values={obj.recharge_on.map(o => `${o}`)}
  //             disabled={disabled}
  //             multiple
  //             onChange={(changed: string[]) => {
  //               const new_obj = obj;
  //               new_obj.recharge_on = changed.map(o => +o);
  //               onChange(new_obj);
  //               setReloading(true);
  //             }}
  //           />
  //         </Grid>
  //       </>
  //     );
  //   }
  //   return null;
  // }

  // const refresh_rule_helper = REFRESH_RULES.includes(obj.special_resource_refresh_rule) ? obj.special_resource_refresh_rule : "Special";
  return (
    <Grid container spacing={0} direction="column">
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 2
          }}>
          <SelectStringBox 
            name="Casting Time"
            options={STANDARD_CASTING_TIMES}
            extra_options={["Special"]}
            value={obj.casting_time}
            disabled={disabled}
            onChange={(changed: CastingTime) => {
              const new_obj = obj;
              new_obj.casting_time = changed;
              onChange(new_obj);
              setPing(!ping);
            }}
          /> 
        </Grid>
        <Grid
          size={{
            xs: 2
          }}>
          <UpgradableNumberBox
            name="Attack Bonus"
            value={obj.attack_bonus}
            disabled={disabled}
            attached_table={attached_table}
            quick_options={["Attack STR","Attack DEX","Attack INT","Attack WIS","Attack CHA"]}
            onChange={(changed: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.attack_bonus = changed;
              onChange(new_obj);
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 2
          }}>
          <UpgradableNumberBox
            name="DC"
            value={obj.dc}
            disabled={disabled}
            attached_table={attached_table}
            quick_options={["DC STR","DC DEX","DC CON","DC INT","DC WIS","DC CHA"]}
            onChange={(changed: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.dc = changed;
              onChange(new_obj);
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 4
          }}>
          <Grid
            size={{
              xs: 6
            }}>
            <StringBox 
              value={obj.range} 
              name="Range"
              disabled={disabled}
              onBlur={(changed: string) => {
                const new_obj = obj;
                new_obj.range = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 6
            }}>
            <StringBox 
              value={obj.range_2} 
              name="Area"
              disabled={disabled}
              onBlur={(changed: string) => {
                const new_obj = obj;
                new_obj.range_2 = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
        </Grid>
        <Grid
          size={{
            xs: 2
          }}>
          <SelectStringBox 
            name="Components"
            options={COMPONENTS}
            values={obj.components}
            multiple
            disabled={disabled}
            onChange={(changed: string[]) => {
              const new_obj = obj;
              new_obj.components = changed;
              onChange(new_obj);
              setPing(!ping);
            }}
          /> 
        </Grid>
        { obj.casting_time === "Special" && 
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox 
              name="Special Casting Time"
              value={obj.special_casting_time}
              disabled={disabled}
              onBlur={(changed: string) => {
                const new_obj = obj;
                new_obj.special_casting_time = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            /> 
          </Grid>
        }
      </Grid>
      <Grid container spacing={0} direction="row">
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 6
          }}>
          <Grid
            size={{
              xs: obj.duration === "Instantaneous" ? 12 : 9
            }}>
            <SelectStringBox 
              name="Duration"
              options={DURATIONS}
              disabled={disabled}
              value={ !DURATIONS.includes(obj.duration) ? "Special" : obj.duration }
              onChange={(changed: DurationType) => {
                const new_obj = obj;
                new_obj.duration = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            /> 
          </Grid>
          { obj.duration !== "Instantaneous" && 
            <Grid
              size={{
                xs: 3
              }}>
              <CheckBox 
                name="C" 
                value={obj.concentration} 
                disabled={disabled}
                onChange={(changed: boolean) => {
                  const new_obj = obj;
                  new_obj.concentration = changed;
                  onChange(new_obj);
                  setPing(!ping);
                }} 
              />
            </Grid>
          } 
          { (obj.duration === "Special" || !DURATIONS.includes(obj.duration)) && 
            <Grid
              size={{
                xs: 12
              }}>
              <StringBox 
                name="Duration"
                disabled={disabled}
                value={obj.duration === "Special" ? "" : obj.duration}
                onBlur={(changed: DurationType) => {
                  const new_obj = obj;
                  new_obj.duration = changed;
                  onChange(new_obj);
                  setPing(!ping);
                }}
              /> 
            </Grid>
          } 
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          <SelectStringBox 
            name="Saving Throw"
            options={ABILITY_SCORES_EXPANDED}
            value={obj.saving_throw_ability_score}
            disabled={disabled}
            onChange={(changed: AbilityScoreExpandedType) => {
              const new_obj = obj;
              new_obj.saving_throw_ability_score = changed;
              onChange(new_obj);
              setPing(!ping);
            }}
          /> 
        </Grid>
      </Grid>
      { obj.components.includes("M") &&
        <Grid>
          <StringBox 
            name="Material Component"
            value={obj.material_component}
            disabled={disabled}
            onBlur={(changed: string) => {
              const new_obj = obj;
              new_obj.material_component = changed;
              onChange(new_obj);
              setPing(!ping);
            }}
          /> 
        </Grid>
      }
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 12
          }}>
          <span className="header">
            Effects
          </span>
          { !disabled &&
            <Tooltip title={`Add Effect`}>
              <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                onClick={ () => {
                  const new_obj = obj;
                  const effect = new AbilityEffect();
                  new_obj.effects.push(effect);
                  onChange(new_obj);
                  setShowEffect(effect.true_id);
                }}>
                <Add/>
              </Fab>
            </Tooltip>
          }
        </Grid>
        { !reloading && obj.effects.map((effect, key) => {
          let name = effect.type === "Damage" ? effect.damage_types.join(", ") : effect.type;
          if (!["None","Self Condition"].includes(effect.type) && effect.attack_type !== "None") {
            name += ` ${effect.attack_type}`
          }

          return (
            <Grid
              key={key}
              container
              spacing={0}
              direction="row"
              size={{
                xs: 12
              }}>
              <Grid
                size={{
                  xs: 12
                }}>
                <ToggleButtonBox
                  name={ name }
                  disabled={disabled}
                  value={ show_effect === effect.true_id }
                  onToggle={ () => { 
                    setShowEffect(show_effect === effect.true_id ? "" : effect.true_id);
                  }}
                />
              </Grid>
              { show_effect === effect.true_id && 
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <AbilityEffectInput 
                    obj={effect}
                    disabled={disabled}
                    onChange={() => {
                      const new_obj = obj;
                      onChange(new_obj);
                      setPing(!ping);
                    }}
                    onDelete={() => {
                      const new_obj = obj;
                      new_obj.effects = new_obj.effects.filter(o => o.true_id !== effect.true_id);
                      onChange(new_obj);
                      setReloading(true);
                    }}
                  />
                </Grid>        
              }
            </Grid>
          );
        })}
      </Grid>
      <Grid>
        <ConsumedResourceInput 
          obj={obj.consumed_resource} 
          disabled={false}
          onChange={(changed: ConsumedResource) => {
            const new_obj = obj;
            new_obj.consumed_resource = changed;
            onChange(new_obj);
            setReloading(true);
          }}
        />
        {/* <SelectObjectBox 
          name="Resource Consumed"
          data_type="resource"
          extra_options={["Special","None","Legendary Action","Recharge"]}
          disabled={disabled}
          value={ obj.resource_consumed ? obj.resource_consumed : "None" }
          onChange={(changed: string) => {
            const new_obj = obj;
            new_obj.resource_consumed = changed;
            if (changed === "Special") {
              if (new_obj.special_resource_refresh_rule === "") {
                new_obj.special_resource_refresh_rule = "Long Rest";
              }
              if (new_obj.special_resource_amount === 0) {
                new_obj.special_resource_amount = 1;
              }
            }
            if (changed !== "None") {
              if (new_obj.amount_consumed === 0) {
                new_obj.amount_consumed = 1;
              }
            }
            onChange(new_obj);
            setPing(!ping);
          }}
        /> */}
      </Grid>
      {/* { obj.resource_consumed && obj.resource_consumed !== "None" && obj.resource_consumed !== "Recharge" &&
        <Grid item>
          <StringBox 
            name="Amount Consumed"
            value={obj.amount_consumed ? `${obj.amount_consumed}` : null} 
            type="number"
            disabled={disabled}
            onBlur={(changed: number) => {
              const new_obj = obj;
              new_obj.amount_consumed = +changed;
              onChange(new_obj);
              setPing(!ping);
            }}
          />
        </Grid>
      }
      { obj.resource_consumed && obj.resource_consumed === "Special" &&
        <Grid item>
          <StringBox  
            name="Total Amount"
            value={`${obj.special_resource_amount}`}
            type="number"
            disabled={disabled}
            onBlur={(changed: string) => {
              const new_obj = obj;
              new_obj.special_resource_amount = +changed;
              onChange(new_obj);
              setPing(!ping);
            }}
          />
        </Grid>
      }
      { obj.resource_consumed && obj.resource_consumed === "Special" &&
        <Grid item>
          <SelectStringBox 
            options={REFRESH_RULES}
            value={ special_refresh_rule ? "Special" : refresh_rule_helper } 
            name="Refresh Rule"
            disabled={disabled}
            onChange={(changed: string) => {
              if (changed === "Special") {
                setSpecialRefreshRule(true);
              } else {
                const new_obj = obj;
                new_obj.special_resource_refresh_rule = changed;
                onChange(new_obj);
                if (special_refresh_rule) {
                  setSpecialRefreshRule(false);
                } else {
                  setPing(!ping);
                }
              }
            }}
          />
        </Grid>
      }
      { renderRecharge() } */}
    </Grid>
  );
}

